/** biome-ignore-all lint/correctness/useExhaustiveDependencies: ff */
import { useEffect, useMemo, useRef, useState } from 'react';
import { cx } from 'class-variance-authority';
import * as v from 'valibot';

import {
  Button,
  type ColumnDef,
  DataTable,
  Icon,
  Modal,
  Pagination,
  Radio,
  RadioGroup,
  Spinner,
  Text,
} from '@nvision/ui';

import { NoData } from '@/modules/errors';

import { useCountries, useGetPorts, usePortTypes, useStates } from '../../../hooks';
import { type PortRow, type PortSearchFormValues, type PortsParams, portSearchParamsSchema } from '../../../models';
import { PortSearchForm, type SearchFormRef } from '../port-search-form';

import styles from './port-search-modal.module.css';

const EMPTY_CELL = '—';

const SORTABLE_COLUMNS = new Set(['city', 'code', 'countryCode', 'name', 'state', 'type', 'zip']);

interface PortSearchModalProps {
  defaultValues?: Partial<PortSearchFormValues>;
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onSelectionChange?: (value: PortRow) => void;
}

export const PortSearchModal: React.FC<PortSearchModalProps> = ({
  defaultValues,
  isOpen = false,
  setIsOpen,
  onSelectionChange,
}) => {
  const [params, setParams] = useState<PortsParams>(v.getDefaults(portSearchParamsSchema));
  const [selectedPort, setSelectedPort] = useState<PortRow | null>(null);
  const selectedPortKeyRef = useRef<number | null>(null);

  const { data: ports, isFetching } = useGetPorts(params);

  const { data: countries } = useCountries();
  const { data: states } = useStates({ CountryCode: params.country ?? '' });
  const { data: portTypes } = usePortTypes();

  const searchFormRef = useRef<SearchFormRef>({} as SearchFormRef);

  const [sortColumnId, sortDirection] = params.sorting?.split(' ') ?? [];
  const tableSorting: { desc: boolean; id: string }[] = sortColumnId
    ? [{ desc: sortDirection === 'desc', id: sortColumnId }]
    : [];

  const reset = () => {
    setSelectedPort(null);
    setParams(v.getDefaults(portSearchParamsSchema));
  };

  const close = () => {
    searchFormRef.current?.reset?.();
    setSelectedPort(null);
    setIsOpen(false);
  };

  const submit = (values: PortSearchFormValues) => {
    setParams((prev) => ({
      ...prev,
      country: values.country?.id ? String(values.country?.id) : '',
      offset: 0,
      portCity: values.portCity,
      portCode: values.portCode,
      portName: values.portName,
      portPostal: values.portPostal,
      portState: values.portState?.id ? String(values.portState?.id) : undefined,
      portType: values.portType?.id ? String(values.portType?.id) : undefined,
      sorting: undefined,
    }));
  };

  const handleSortingChange = (newSorting: { desc: boolean; id: string }[]) => {
    setParams((prev) => {
      const [sort] = newSorting;
      if (!sort || !SORTABLE_COLUMNS.has(sort.id)) {
        return { ...prev, sorting: undefined };
      }

      return { ...prev, sorting: `${sort.id} ${sort.desc ? 'desc' : 'asc'}` };
    });
  };

  const handleSubmitSelection = () => {
    if (selectedPort && onSelectionChange) {
      onSelectionChange(selectedPort);
      close();
    }
  };

  useEffect(() => {
    selectedPortKeyRef.current = selectedPort?.portKey || null;
  }, [selectedPort]);

  // biome-ignore lint/suspicious/noExplicitAny: TODO: fix
  const tableColumns: ColumnDef<PortRow, any>[] = useMemo(
    () => [
      {
        accessorKey: 'portKey',
        cell: (props) => {
          const row = props.row.original;
          const isSelected = selectedPortKeyRef.current === row.portKey;

          return (
            <RadioGroup
              value={isSelected ? String(row.portKey) : ''}
              onChange={() => {
                setSelectedPort(row);
                selectedPortKeyRef.current = row.portKey;
              }}
              className={styles.radio}
            >
              <Radio value={String(row.portKey)} className={styles.radio} />
            </RadioGroup>
          );
        },
        enableSorting: false,
        header: '',
        maxSize: 0,
      },
      {
        accessorKey: 'type',
        cell: (props) => {
          const { type } = props.row.original;
          return (type && portTypes?.map?.[type]?.label) || type || EMPTY_CELL;
        },
        header: 'Port Type',
        sortingFn: () => 0,
      },
      {
        accessorKey: 'code',
        cell: (props) => props.row.original.code || EMPTY_CELL,
        header: 'Port Code',
        sortingFn: () => 0,
      },
      {
        accessorKey: 'name',
        cell: (props) => props.row.original.name || EMPTY_CELL,
        header: 'Port Name',
        sortingFn: () => 0,
      },
      {
        accessorKey: 'zip',
        cell: (props) => props.row.original.zip || EMPTY_CELL,
        header: 'Port Postal',
        sortingFn: () => 0,
      },
      {
        accessorKey: 'city',
        cell: (props) => props.row.original.city || EMPTY_CELL,
        header: 'Port City',
        sortingFn: () => 0,
      },
      {
        accessorKey: 'state',
        cell: (props) => {
          const { state } = props.row.original;
          return states?.map[String(state)]?.label || state || EMPTY_CELL;
        },
        header: 'Port State',
        sortingFn: () => 0,
      },
      {
        accessorKey: 'countryCode',
        cell: (props) => {
          const { countryCode } = props.row.original;
          return countries?.map[String(countryCode)]?.label || countryCode || EMPTY_CELL;
        },
        header: 'Country',
        sortingFn: () => 0,
      },
    ],
    [countries, states, portTypes?.map],
  );

  return (
    <Modal isOpen={isOpen} onOpenChange={(value) => setIsOpen(value)} className={styles.modal}>
      <Modal.Header className={styles.header}>
        <Text size="xlg" weight="semibold">
          Port Search
        </Text>
        <Icon name="x" onClick={close} cursor="pointer" />
      </Modal.Header>
      <Modal.Content className={styles.content}>
        <PortSearchForm
          defaultValues={defaultValues}
          formRef={searchFormRef}
          handleSubmit={submit}
          handleReset={reset}
        />
        {isFetching ? (
          <div className={cx(styles.tableContainer, styles.loader)}>
            <Spinner size="lg" />
          </div>
        ) : ports?.data.length ? (
          <div className={styles.results}>
            <div className={styles.total}>
              {ports.total === 1 ? (
                <>
                  <Text weight="bold">{`${ports.total} port `}</Text>is found
                </>
              ) : (
                <>
                  <Text weight="bold">{`${ports.total} ports `}</Text>are found
                </>
              )}
            </div>
            <div className={styles.tableContainer}>
              <DataTable<PortRow>
                columns={tableColumns}
                data={ports.data}
                isLoading={isFetching}
                page={params.offset / params.limit + 1}
                limit={params.limit}
                totalItems={ports.total}
                sorting={tableSorting}
                onSortingChange={handleSortingChange}
              />
            </div>

            <Pagination
              className={styles.pagination}
              totalItems={ports.total}
              limit={params.limit}
              page={params.offset / params.limit + 1}
              onPageChange={(page: number) => {
                setParams((prev) => ({ ...prev, offset: (page - 1) * prev.limit }));
              }}
              onOffsetChange={(offset: number) => setParams((prev) => ({ ...prev, offset }))}
              onLimitChange={(limit: number) => setParams((prev) => ({ ...prev, limit, offset: 0 }))}
            />
          </div>
        ) : (
          <NoData
            className={styles.noData}
            description="No data to display. Please use the search above to find ports."
          />
        )}
      </Modal.Content>
      <Modal.Footer className={styles.footer}>
        <Button outline secondary onClick={close}>
          Cancel
        </Button>
        <Button isDisabled={!selectedPort} onClick={handleSubmitSelection}>
          Submit
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
